// frontend/src/components/assessment/artifacts/InnerMonologueScript.js
// Phase inner_monologue artifact: read-only script with editable per-turn thought column.
//
// Two-column layout per turn row:
//   Left  — editable "thoughts:" textarea (one per me: turn, one per them: turn)
//   Right — read-only turn content (me: or them:)
//
// Data shape per pair: { pairId, me, them, me_thought, them_thought }
// Controls for reordering, inserting, and deleting pairs are intentionally absent.
// An undo toast is provided for accidental thought clears (clear button per thought).

import { useState, useRef, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import useSpeechDictation from '../../../hooks/useSpeechDictation';


// ---------------------------------------------------------------------------
// ProgressBar — passive indicator of how many thought slots are filled
// ---------------------------------------------------------------------------

const ProgressBar = ({ script }) => {
  const totalSlots = script.length * 2;
  const filledSlots = script.reduce((count, pair) => {
    if (pair.me_thought?.content?.trim().length > 0) count++;
    if (pair.them_thought?.content?.trim().length > 0) count++;
    return count;
  }, 0);

  const pct = totalSlots > 0 ? (filledSlots / totalSlots) * 100 : 0;

  return (
    <div className="im-progress-block">
      <span className="im-progress-text">{filledSlots} of {totalSlots} thoughts captured</span>
      <div className="im-progress-track">
        <div className="im-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  script: PropTypes.array.isRequired,
};


// ---------------------------------------------------------------------------
// ReadOnlyTurnContent — non-editable turn text
// ---------------------------------------------------------------------------

const ReadOnlyTurnContent = ({ content, source }) => {
  const contentClass = [
    'im-readonly-content',
    source === 'ai'   ? 'ai-sourced'   : '',
    source === 'user' ? 'user-sourced' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={contentClass}>
      {content || <span className="im-readonly-empty">—</span>}
    </div>
  );
};

ReadOnlyTurnContent.propTypes = {
  content: PropTypes.string,
  source:  PropTypes.oneOf(['none', 'ai', 'user']).isRequired,
};


// ---------------------------------------------------------------------------
// ThoughtCell — editable textarea for one turn's inner thought
// ---------------------------------------------------------------------------

const ThoughtCell = ({ pairId, side, thought, onSave, isListening, interimTranscript, readOnly = false }) => {
  const textareaRef   = useRef(null);
  const [localValue,  setLocalValue]  = useState(thought?.content || '');
  const [isFocused,   setIsFocused]   = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const prevContentRef = useRef(thought?.content);

  // Sync from parent when not actively editing (AI updates)
  useEffect(() => {
    if (!isFocused) setLocalValue(thought?.content || '');
  }, [thought?.content, isFocused]);

  // Brief highlight pulse when AI updates existing content
  useEffect(() => {
    const cur  = thought?.content;
    const prev = prevContentRef.current;
    if (
      thought?.source === 'ai' &&
      cur && cur.length > 0 &&
      prev && prev.length > 0 &&
      cur !== prev
    ) {
      setHighlighted(true);
      const t = setTimeout(() => setHighlighted(false), 700);
      prevContentRef.current = cur;
      return () => clearTimeout(t);
    }
    prevContentRef.current = cur;
  }, [thought]);

  // Auto-expand textarea height to fit content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.max(textareaRef.current.scrollHeight, 44) + 'px';
    }
  }, [localValue]);

  const cellClass = [
    'im-thought-cell',
    thought?.source === 'ai'   ? 'ai-sourced'   : '',
    thought?.source === 'user' ? 'user-sourced' : '',
    highlighted   ? 'highlighted'  : '',
    isListening   ? 'im-listening' : '',
  ].filter(Boolean).join(' ');

  // While dictating, show a live preview of existing + interim text without mutating localValue
  const displayValue = isListening && interimTranscript
    ? (localValue ? localValue + ' ' + interimTranscript : interimTranscript)
    : localValue;

  return (
    <div className={cellClass}>
      <textarea
        ref={textareaRef}
        id={`im-thought-${pairId}-${side}`}
        className={`script-turn-textarea im-thought-textarea${isFocused ? ' focused' : ''}`}
        rows={1}
        placeholder={isListening ? 'Listening…' : 'What are you thinking?'}
        value={displayValue}
        disabled={isListening || readOnly}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onSave(pairId, side, e.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onSave(pairId, side, localValue);
        }}
      />
    </div>
  );
};

ThoughtCell.propTypes = {
  pairId:           PropTypes.string.isRequired,
  side:             PropTypes.oneOf(['me_thought', 'them_thought']).isRequired,
  thought:          PropTypes.shape({
    content: PropTypes.string,
    source:  PropTypes.oneOf(['none', 'ai', 'user']),
  }),
  onSave:           PropTypes.func.isRequired,
  isListening:      PropTypes.bool,
  interimTranscript: PropTypes.string,
  readOnly:         PropTypes.bool,
};


// ---------------------------------------------------------------------------
// UndoToast
// ---------------------------------------------------------------------------

const UndoToast = ({ onUndo }) => (
  <div className="script-undo-toast">
    <span>Thought cleared.</span>
    <button className="script-undo-btn" onClick={onUndo}>Undo</button>
  </div>
);

UndoToast.propTypes = {
  onUndo: PropTypes.func.isRequired,
};


// ---------------------------------------------------------------------------
// MonologuePair — two turn rows, each with its own thought on the left
// ---------------------------------------------------------------------------

const MonologuePair = ({ pair, isNew, onThoughtSave, onThoughtClear, activeMic, onMicToggle, interimTranscript, isMicSupported, readOnly = false }) => {
  const pairClass = ['im-pair', isNew ? 'ai-appended' : ''].filter(Boolean).join(' ');

  const isMeListening   = activeMic?.pairId === pair.pairId && activeMic?.side === 'me_thought';
  const isThemListening = activeMic?.pairId === pair.pairId && activeMic?.side === 'them_thought';

  const meHasContent   = pair.me_thought?.content?.trim().length > 0;
  const themHasContent = pair.them_thought?.content?.trim().length > 0;

  return (
    <div className={pairClass}>
      {/* me turn row */}
      <div className="im-turn-row">
        <div className="im-thought-col">
          <div className="im-label-col">
            <label htmlFor={`im-thought-${pair.pairId}-me_thought`} className="script-speaker-label im-thought-label">thoughts:</label>
            <div className="im-label-actions">
              {isMicSupported && (
                <button
                  className={`im-mic-btn${isMeListening ? ' listening' : ''}`}
                  title={isMeListening ? 'Stop dictating' : 'Dictate thought'}
                  tabIndex={-1}
                  onClick={() => onMicToggle(pair.pairId, 'me_thought')}
                >
                  {isMeListening ? '■' : '🎤'}
                </button>
              )}
              {!readOnly && !isMeListening && meHasContent && (
                <button
                  className="im-clear-btn"
                  title="Clear this thought"
                  tabIndex={-1}
                  onClick={() => onThoughtClear(pair.pairId, 'me_thought', pair.me_thought?.content || '')}
                >×</button>
              )}
            </div>
          </div>
          <ThoughtCell
            pairId={pair.pairId}
            side="me_thought"
            thought={pair.me_thought}
            onSave={onThoughtSave}
            isListening={isMeListening}
            interimTranscript={isMeListening ? interimTranscript : ''}
            readOnly={readOnly}
          />
        </div>
        <div className="im-turn-col im-turn-me">
          <span className="script-speaker-label script-speaker-me">me:</span>
          <ReadOnlyTurnContent
            content={pair.me?.content}
            source={pair.me?.source || 'none'}
          />
        </div>
      </div>

      {/* them turn row */}
      <div className="im-turn-row">
        <div className="im-thought-col">
          <div className="im-label-col">
            <label htmlFor={`im-thought-${pair.pairId}-them_thought`} className="script-speaker-label im-thought-label">thoughts:</label>
            <div className="im-label-actions">
              {isMicSupported && (
                <button
                  className={`im-mic-btn${isThemListening ? ' listening' : ''}`}
                  title={isThemListening ? 'Stop dictating' : 'Dictate thought'}
                  tabIndex={-1}
                  onClick={() => onMicToggle(pair.pairId, 'them_thought')}
                >
                  {isThemListening ? '■' : '🎤'}
                </button>
              )}
              {!readOnly && !isThemListening && themHasContent && (
                <button
                  className="im-clear-btn"
                  title="Clear this thought"
                  tabIndex={-1}
                  onClick={() => onThoughtClear(pair.pairId, 'them_thought', pair.them_thought?.content || '')}
                >×</button>
              )}
            </div>
          </div>
          <ThoughtCell
            pairId={pair.pairId}
            side="them_thought"
            thought={pair.them_thought}
            onSave={onThoughtSave}
            isListening={isThemListening}
            interimTranscript={isThemListening ? interimTranscript : ''}
            readOnly={readOnly}
          />
        </div>
        <div className="im-turn-col im-turn-them">
          <span className="script-speaker-label script-speaker-them">them:</span>
          <ReadOnlyTurnContent
            content={pair.them?.content}
            source={pair.them?.source || 'none'}
          />
        </div>
      </div>
    </div>
  );
};

MonologuePair.propTypes = {
  pair:             PropTypes.object.isRequired,
  isNew:            PropTypes.bool,
  onThoughtSave:    PropTypes.func.isRequired,
  onThoughtClear:   PropTypes.func.isRequired,
  activeMic:        PropTypes.shape({ pairId: PropTypes.string, side: PropTypes.string }),
  onMicToggle:      PropTypes.func.isRequired,
  interimTranscript: PropTypes.string,
  isMicSupported:   PropTypes.bool,
  readOnly:         PropTypes.bool,
};


// ---------------------------------------------------------------------------
// InnerMonologueScript — exported main component
// ---------------------------------------------------------------------------

const InnerMonologueScript = forwardRef(({
  script,
  conversationId,
  onClientThoughtChange,
  onSaveStatusChange,
  onCompletenessChange,
  readOnly = false,
}, ref) => {
  const { user } = useAuth();
  const bottomRef      = useRef(null);
  const saveTimerRef   = useRef(null);
  const prevScriptRef  = useRef(script);
  const scriptRef      = useRef(script);
  const [undoPending, setUndoPending] = useState(null); // { pairId, side, previousContent, timerId }
  const [newPairIds,  setNewPairIds]  = useState(new Set());
  const [activeMic,   setActiveMic]   = useState(null); // { pairId, side }

  const { isSupported: isMicSupported, isListening, interimTranscript, start: startDictation, stop: stopDictation } = useSpeechDictation();

  // Keep scriptRef current so timer callbacks always see the latest state
  useEffect(() => { scriptRef.current = script; }, [script]);

  // Completeness — derived from script, emitted to parent via onCompletenessChange
  const completeness = useMemo(() => {
    const totalSlots = script.length * 2;
    let meThoughtsFilled = 0;
    let themThoughtsFilled = 0;
    script.forEach((pair) => {
      if (pair.me_thought?.content?.trim().length > 0) meThoughtsFilled++;
      if (pair.them_thought?.content?.trim().length > 0) themThoughtsFilled++;
    });
    const filledSlots = meThoughtsFilled + themThoughtsFilled;
    return { totalSlots, filledSlots, meThoughtsFilled, themThoughtsFilled, minimumMet: filledSlots >= 3 };
  }, [script]);

  useEffect(() => {
    if (onCompletenessChange) onCompletenessChange(completeness);
  }, [completeness, onCompletenessChange]);

  // Detect AI-appended pairs: animate and auto-scroll
  useEffect(() => {
    const prev = prevScriptRef.current;
    if (script.length > prev.length) {
      const added = script.slice(prev.length);
      setNewPairIds(new Set(added.map(p => p.pairId)));
      const clearTimer = setTimeout(() => setNewPairIds(new Set()), 400);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      prevScriptRef.current = script;
      return () => clearTimeout(clearTimer);
    }
    prevScriptRef.current = script;
  }, [script]);

  // Debounced autosave — reads scriptRef.current for latest state. Split so flush()
  // (exposed via ref) can force a pending save to settle before an AI turn; see
  // AssessmentView.handleSendMessage.
  const dirtyRef    = useRef(false);
  const inflightRef = useRef(null);

  const doSave = useCallback(() => {
    dirtyRef.current = false;
    onSaveStatusChange('saving');
    const pairs = scriptRef.current;
    const req = apiService.patch(
      `/api/v1/conversations/${conversationId}/script`,
      {
        pairs: pairs.map(p => ({
          pairId:      p.pairId,
          me:          p.me,
          them:        p.them,
          me_thought:   p.me_thought   || { content: null, source: 'none' },
          them_thought: p.them_thought || { content: null, source: 'none' },
        })),
      },
      user
    )
      .then(() => {
        onSaveStatusChange('saved');
        setTimeout(() => onSaveStatusChange(null), 2000);
      })
      .catch((err) => {
        console.error('[InnerMonologueScript] Autosave failed:', err);
        onSaveStatusChange(null);
      });
    inflightRef.current = req;
    return req;
  }, [conversationId, user, onSaveStatusChange]);

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  const flush = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (dirtyRef.current) return doSave();
    if (inflightRef.current) return inflightRef.current;
    return undefined;
  }, [doSave]);

  useImperativeHandle(ref, () => ({ flush }), [flush]);

  const handleThoughtSave = useCallback((pairId, side, value) => {
    onClientThoughtChange(pairId, side, value);
    scheduleSave();
  }, [onClientThoughtChange, scheduleSave]);

  const handleThoughtClear = useCallback((pairId, side, previousContent) => {
    onClientThoughtChange(pairId, side, '');

    const timerId = setTimeout(() => {
      scheduleSave();
      setUndoPending(null);
    }, 5000);

    setUndoPending({ pairId, side, previousContent, timerId });
  }, [onClientThoughtChange, scheduleSave]);

  const handleUndo = useCallback(() => {
    if (!undoPending) return;
    clearTimeout(undoPending.timerId);
    onClientThoughtChange(undoPending.pairId, undoPending.side, undoPending.previousContent);
    scheduleSave();
    setUndoPending(null);
  }, [undoPending, onClientThoughtChange, scheduleSave]);

  const handleMicToggle = useCallback((pairId, side) => {
    const isThisActive = activeMic?.pairId === pairId && activeMic?.side === side;
    if (isThisActive || isListening) {
      stopDictation();
      setActiveMic(null);
      return;
    }
    // Look up existing content so we can append to it on completion
    const pair = scriptRef.current.find(p => p.pairId === pairId);
    const existingContent = pair?.[side]?.content || '';

    startDictation((finalText) => {
      const appended = existingContent
        ? existingContent.trimEnd() + ' ' + finalText
        : finalText;
      onClientThoughtChange(pairId, side, appended);
      scheduleSave();
      setActiveMic(null);
    });
    setActiveMic({ pairId, side });
  }, [activeMic, isListening, startDictation, stopDictation, onClientThoughtChange, scheduleSave]);

  return (
    <div className="narration-script inner-monologue-script">
      {script.length === 0 && (
        <p className="script-empty-prompt">
          The conversation will appear here once it has been transcribed.
        </p>
      )}

      {script.length > 0 && <ProgressBar script={script} />}

      {script.map((pair) => (
        <MonologuePair
          key={pair.pairId}
          pair={pair}
          isNew={newPairIds.has(pair.pairId)}
          onThoughtSave={handleThoughtSave}
          onThoughtClear={handleThoughtClear}
          activeMic={activeMic}
          onMicToggle={handleMicToggle}
          interimTranscript={interimTranscript}
          isMicSupported={isMicSupported && !readOnly}
          readOnly={readOnly}
        />
      ))}

      <div ref={bottomRef} />

      {undoPending && <UndoToast onUndo={handleUndo} />}
    </div>
  );
});

InnerMonologueScript.displayName = 'InnerMonologueScript';

InnerMonologueScript.propTypes = {
  script: PropTypes.arrayOf(PropTypes.shape({
    pairId:       PropTypes.string.isRequired,
    me:           PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }).isRequired,
    them:         PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }).isRequired,
    me_thought:   PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }),
    them_thought: PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }),
  })).isRequired,
  conversationId:        PropTypes.string.isRequired,
  onClientThoughtChange: PropTypes.func.isRequired,
  onSaveStatusChange:    PropTypes.func.isRequired,
  onCompletenessChange:  PropTypes.func,
  readOnly:              PropTypes.bool,
};

export default InnerMonologueScript;